// src/pages/nascar/CupArticleIndex.jsx
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";

const INDEX_URL = "/data/nascar/cup/articles/index.json";

// ✅ Local-safe date parser for "YYYY-MM-DD" (prevents timezone shifting to prior day)
function parseLocalYMD(ymd) {
  if (!ymd || typeof ymd !== "string") return null;
  const [y, m, d] = ymd.split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

const SkeletonCard = ({ isLight }) => (
  <div
    className={
      "animate-pulse rounded-2xl border p-5 " +
      (isLight
        ? "border-slate-200 bg-white shadow-[0_10px_35px_rgba(15,23,42,0.12)]"
        : "border-neutral-800 bg-neutral-900/50")
    }
  >
    <div
      className={
        "mb-2 h-4 w-1/3 rounded " + (isLight ? "bg-slate-200" : "bg-neutral-800")
      }
    />
    <div
      className={
        "mb-3 h-5 w-3/4 rounded " + (isLight ? "bg-slate-200" : "bg-neutral-800")
      }
    />
    <div
      className={
        "h-3 w-full rounded " + (isLight ? "bg-slate-200" : "bg-neutral-800")
      }
    />
  </div>
);

export default function CupArticleIndex() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");

  // local theme for this page
  const [theme, setTheme] = useState("dark");
  const isLight = theme === "light";

  useEffect(() => {
    let alive = true;

    async function load() {
      try {
        setLoading(true);
        setErrorMsg("");

        const res = await fetch(INDEX_URL, { cache: "no-cache" });

        if (!alive) return;

        if (!res.ok) {
          setErrorMsg(
            `Could not load articles index from ${INDEX_URL}. Make sure the file exists.`
          );
          setArticles([]);
          return;
        }

        const data = await res.json();

        // ensure array
        const list = Array.isArray(data) ? data : [];

        // ✅ sort by published_at desc using local-safe parsing (newest first)
        list.sort((a, b) => {
          const db = parseLocalYMD(b?.published_at);
          const da = parseLocalYMD(a?.published_at);
          if (!db || !da) return 0;
          return db - da;
        });

        setArticles(list);
      } catch (err) {
        console.error(err);
        if (!alive) return;
        setErrorMsg("Error loading articles. Please try again later.");
        setArticles([]);
      } finally {
        if (alive) setLoading(false);
      }
    }

    load();
    return () => {
      alive = false;
    };
  }, []);

  // ---------- Featured (pinned or most recent) ----------
  let featuredArticle = null;
  let otherArticles = articles;

  if (articles.length > 0) {
    // Prefer a pinned article, otherwise newest
    featuredArticle = articles.find((a) => a.pinned) || articles[0];
    otherArticles = articles.filter((a) => a !== featuredArticle);
  }

  const pageBgClass = isLight
    ? "bg-slate-50 text-slate-900"
    : "bg-neutral-950 text-neutral-50";

  const errorBoxClass = isLight
    ? "border-red-300 bg-red-50 text-red-800"
    : "border-red-600/40 bg-red-900/20 text-red-200";

  const emptyBoxClass = isLight
    ? "border-slate-200 bg-white text-slate-700 shadow-[0_10px_35px_rgba(15,23,42,0.08)]"
    : "border-neutral-800 bg-neutral-900/60 text-neutral-300";

  const featuredCardClass = isLight
    ? "border-blue-200 bg-white shadow-[0_18px_55px_rgba(37,99,235,0.18)]"
    : "border-blue-500/40 bg-neutral-900/70 shadow-lg shadow-blue-500/5";

  const gridCardClass = isLight
    ? "border-slate-200 bg-white shadow-[0_12px_40px_rgba(15,23,42,0.12)]"
    : "border-neutral-800 bg-neutral-900/60";

  return (
    <div className={`min-h-screen ${pageBgClass}`}>
      {/* dark background glow like article page */}
      {!isLight && (
        <div className="pointer-events-none fixed inset-0 -z-10 bg-[radial-gradient(circle_at_top,_rgba(59,130,246,0.18),_transparent_55%),radial-gradient(circle_at_bottom,_rgba(56,189,248,0.16),_transparent_60%)]" />
      )}

      <div className="mx-auto w-full max-w-6xl px-4 pb-24 pt-10 lg:max-w-7xl lg:px-6">
        {/* Top row: breadcrumb + theme toggle */}
        <div className="mb-4 flex items-center justify-between gap-3">
          {/* Breadcrumb */}
          <div className="flex flex-wrap items-center gap-3 text-sm">
            <Link
              to="/nascar"
              className="font-medium text-blue-500 hover:text-blue-400"
            >
              ← NASCAR
            </Link>
            <span className="hidden h-1 w-1 rounded-full bg-neutral-400 sm:inline-block" />
            <span className="hidden text-neutral-500 sm:inline-block">
              Cup Series
            </span>
          </div>

          {/* Theme toggle */}
          <button
            type="button"
            onClick={() =>
              setTheme((prev) => (prev === "dark" ? "light" : "dark"))
            }
            className="inline-flex items-center gap-2 rounded-full border border-neutral-700/50 bg-neutral-900/70 px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-neutral-200 shadow-sm transition hover:border-blue-500/70 hover:text-blue-100 hover:shadow-[0_0_20px_rgba(59,130,246,0.45)] sm:text-[0.7rem]"
          >
            <span
              className={`text-base ${
                isLight ? "text-amber-400" : "text-blue-300"
              }`}
            >
              {isLight ? "☀" : "☾"}
            </span>
            <span>{isLight ? "Light mode" : "Dark mode"}</span>
          </button>
        </div>

        {/* Header */}
        <header className="mb-8 space-y-4">
          <div
            className={`inline-flex flex-wrap items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wide shadow-sm
              ${
                isLight
                  ? "border border-blue-300 bg-blue-100 text-blue-700"
                  : "border border-blue-500/30 bg-blue-500/10 text-blue-200"
              }`}
          >
            <span>Articles</span>
            <span className="text-neutral-400">•</span>
            <span>Cup Series</span>
          </div>

          <h1 className="text-balance text-3xl font-extrabold tracking-tight sm:text-4xl md:text-5xl">
            Cup Series Articles
          </h1>

          <p
            className={
              "max-w-2xl text-sm sm:text-base " +
              (isLight ? "text-slate-700" : "text-neutral-300")
            }
          >
            Browse all NASCAR Cup race breakdowns and strategy notes. Click an
            article below to open the full write-up.
          </p>
        </header>

        {/* Content */}
        {errorMsg && !loading && (
          <div
            className={`mb-6 rounded-xl border px-4 py-3 text-sm ${errorBoxClass}`}
          >
            {errorMsg}
          </div>
        )}

        {loading ? (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 4 }).map((_, i) => (
              <SkeletonCard key={i} isLight={isLight} />
            ))}
          </div>
        ) : articles.length === 0 ? (
          <div className={`rounded-2xl border p-6 text-sm ${emptyBoxClass}`}>
            No Cup articles found yet.
            <div
              className={
                "mt-2 text-xs " +
                (isLight ? "text-slate-500" : "text-neutral-400")
              }
            >
              Create{" "}
              <code className="rounded bg-neutral-900 px-1 py-0.5 text-neutral-100">
                {INDEX_URL}
              </code>{" "}
              with an array of objects like:
              <pre className="mt-2 overflow-x-auto rounded bg-neutral-900 p-2 text-[0.7rem] leading-snug text-neutral-100">
{`[
  {
    "slug": "2024-phoenix-championship",
    "title": "Cup Championship Race | Phoenix",
    "subtitle": "Short flat track • 312 laps • Championship breakdown.",
    "published_at": "2024-11-03",
    "tags": ["NASCAR Cup", "Race Preview"],
    "read_time": "4 min read",
    "pinned": true
  }
]`}
              </pre>
            </div>
          </div>
        ) : (
          <>
            {/* ---------- FEATURED ARTICLE ---------- */}
            {featuredArticle && (
              <Link
                to={`/nascar/cup/articles/${featuredArticle.slug}`}
                className={
                  "group mb-8 block rounded-2xl border p-5 transition lg:p-6 " +
                  featuredCardClass +
                  " hover:border-blue-400 hover:shadow-[0_22px_65px_rgba(37,99,235,0.35)]"
                }
              >
                <div className="mb-2 flex flex-wrap items-center gap-2 text-xs text-neutral-400">
                  <span
                    className={
                      "rounded-full px-2 py-0.5 text-xs font-semibold " +
                      (isLight
                        ? "bg-blue-100 text-blue-700"
                        : "bg-blue-600/20 text-blue-300")
                    }
                  >
                    FEATURED
                  </span>

                  {/* FREE badge (only when featuredArticle.free === true) */}
                  {featuredArticle.free === true && (
                    <span
                      className={
                        "rounded-full px-2 py-0.5 text-xs font-semibold uppercase tracking-wide ring-1 " +
                        (isLight
                          ? "bg-emerald-100 text-emerald-800 ring-emerald-300"
                          : "bg-emerald-500/15 text-emerald-300 ring-emerald-500/40")
                      }
                    >
                      FREE
                    </span>
                  )}

                  {featuredArticle.published_at && (
                    <>
                      <span className="h-1 w-1 rounded-full bg-neutral-500" />
                      <span>
                        {parseLocalYMD(
                          featuredArticle.published_at
                        )?.toLocaleDateString(undefined, {
                          year: "numeric",
                          month: "short",
                          day: "numeric",
                        })}
                      </span>
                    </>
                  )}

                  {featuredArticle.read_time && (
                    <>
                      <span className="h-1 w-1 rounded-full bg-neutral-500" />
                      <span>{featuredArticle.read_time}</span>
                    </>
                  )}
                </div>

                <h2
                  className={
                    "text-lg font-semibold group-hover:text-blue-500 " +
                    (isLight ? "text-slate-900" : "text-neutral-50")
                  }
                >
                  {featuredArticle.title}
                </h2>

                {featuredArticle.subtitle && (
                  <p
                    className={
                      "mt-1 text-sm line-clamp-2 " +
                      (isLight ? "text-slate-700" : "text-neutral-300")
                    }
                  >
                    {featuredArticle.subtitle}
                  </p>
                )}

                {featuredArticle.tags?.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {featuredArticle.tags.slice(0, 4).map((tag) => (
                      <span
                        key={tag}
                        className={
                          "rounded-full px-2 py-0.5 text-xs " +
                          (isLight
                            ? "bg-blue-50 text-blue-700"
                            : "bg-neutral-800 text-neutral-400")
                        }
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                )}

                <div
                  className={
                    "mt-4 text-xs font-medium group-hover:underline " +
                    (isLight ? "text-blue-700" : "text-blue-300")
                  }
                >
                  Read featured breakdown →
                </div>
              </Link>
            )}

            {/* ---------- OTHER ARTICLES GRID ---------- */}
            {otherArticles.length > 0 && (
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {otherArticles.map((article) => {
                  const {
                    slug,
                    title,
                    subtitle,
                    published_at,
                    tags = [],
                    read_time,
                  } = article;

                  // ✅ Local-safe display date
                  const dateLabel = published_at
                    ? parseLocalYMD(published_at)?.toLocaleDateString(
                        undefined,
                        {
                          year: "numeric",
                          month: "short",
                          day: "numeric",
                        }
                      )
                    : null;

                  return (
                    <Link
                      key={slug}
                      to={`/nascar/cup/articles/${slug}`}
                      className={
                        "group flex flex-col justify-between rounded-2xl border p-5 transition hover:border-blue-500/60 hover:shadow-[0_18px_55px_rgba(37,99,235,0.28)] " +
                        gridCardClass
                      }
                    >
                      <div>
                        <div className="mb-2 flex flex-wrap items-center gap-2 text-xs text-neutral-400">
                          {dateLabel && <span>{dateLabel}</span>}

                          {/* FREE badge (only when article.free === true) */}
                          {article.free === true && (
                            <>
                              <span className="h-1 w-1 rounded-full bg-neutral-500" />
                              <span
                                className={
                                  "rounded-full px-2 py-0.5 text-[0.7rem] font-semibold uppercase tracking-wide ring-1 " +
                                  (isLight
                                    ? "bg-emerald-100 text-emerald-800 ring-emerald-300"
                                    : "bg-emerald-500/15 text-emerald-300 ring-emerald-500/40")
                                }
                              >
                                FREE
                              </span>
                            </>
                          )}

                          {read_time && (
                            <>
                              <span className="h-1 w-1 rounded-full bg-neutral-500" />
                              <span>{read_time}</span>
                            </>
                          )}

                          {tags && tags.length > 0 && (
                            <>
                              <span className="h-1 w-1 rounded-full bg-neutral-500" />
                              <span className="truncate">
                                {tags.slice(0, 3).join(" • ")}
                                {tags.length > 3 ? " +" : ""}
                              </span>
                            </>
                          )}
                        </div>

                        <h2
                          className={
                            "text-base font-semibold sm:text-lg group-hover:text-blue-500 " +
                            (isLight ? "text-slate-900" : "text-neutral-50")
                          }
                        >
                          {title || "Untitled Article"}
                        </h2>

                        {subtitle && (
                          <p
                            className={
                              "mt-1 line-clamp-3 text-sm " +
                              (isLight ? "text-slate-700" : "text-neutral-300")
                            }
                          >
                            {subtitle}
                          </p>
                        )}
                      </div>

                      <div
                        className={
                          "mt-4 flex items-center justify-between text-xs font-medium " +
                          (isLight ? "text-blue-700" : "text-blue-300")
                        }
                      >
                        <span className="group-hover:underline">
                          Read article →
                        </span>
                      </div>
                    </Link>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
